import axios from "axios";
import { useState, useRef, useEffect, useImperativeHandle, forwardRef } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../../config/axios";
import { Link, useNavigate } from "react-router-dom";
import Logo from "../../assets/images/logo.png";
import { Eye, EyeOff, Lock, Mail, Shield, CheckCircle, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

/* ═════════════════════════ Fireworks (canvas) ═════════════════════════
   Rockets launch from behind the horizon and explode in the sky as
   peony / ring / willow / chrysanthemum / double bursts, with light flashes. */
const HUES = [48, 40, 28, 330, 310, 280, 195, 55];
const MAX_SPARKS = 2800;
const rnd = (a, b) => a + Math.random() * (b - a);

const FireworksCanvas = forwardRef(function FireworksCanvas({ auto = true }, ref) {
  const canvasRef = useRef(null);
  const sparks = useRef([]);
  const rockets = useRef([]);
  const flashes = useRef([]);
  const dims = useRef({ w: 0, h: 0, horizon: 0 });

  const addSpark = (x, y, vx, vy, hue, o = {}) => {
    if (sparks.current.length > MAX_SPARKS) return;
    sparks.current.push({
      x, y, vx, vy, hue,
      life: 1,
      decay: o.decay ?? rnd(0.009, 0.018),
      size: o.size ?? rnd(1.2, 2.4),
      g: o.g ?? 0.04,
      drag: o.drag ?? 0.985,
      light: o.light ?? 62,
      trail: o.trail ?? false,
    });
  };

  const explode = (x, y, forced) => {
    const hue = HUES[Math.floor(Math.random() * HUES.length)];
    const types = ["peony", "peony", "ring", "willow", "chrys", "double"];
    const type = forced || types[Math.floor(Math.random() * types.length)];
    const small = dims.current.w < 700;
    const k = small ? 0.7 : 1;
    const sp = small ? 0.8 : 1;
    flashes.current.push({ x, y, life: 1, hue });

    if (type === "peony") {
      for (let i = 0; i < 100 * k; i++) {
        const a = rnd(0, Math.PI * 2), s = rnd(1.2, 4.8) * sp;
        addSpark(x, y, Math.cos(a) * s, Math.sin(a) * s, hue + rnd(-8, 8));
      }
    } else if (type === "ring") {
      const m = Math.round(72 * k);
      for (let i = 0; i < m; i++) {
        const a = (i / m) * Math.PI * 2;
        addSpark(x, y, Math.cos(a) * 3.8 * sp, Math.sin(a) * 3.8 * sp, hue, { decay: 0.012 });
      }
      for (let i = 0; i < 20 * k; i++) {
        const a = rnd(0, Math.PI * 2), s = rnd(0.4, 1.6);
        addSpark(x, y, Math.cos(a) * s, Math.sin(a) * s, 50, { decay: 0.016, light: 80 });
      }
    } else if (type === "willow") {
      for (let i = 0; i < 90 * k; i++) {
        const a = rnd(0, Math.PI * 2), s = rnd(1.5, 4.2) * sp;
        addSpark(x, y, Math.cos(a) * s, Math.sin(a) * s, 44 + rnd(-6, 6), {
          decay: rnd(0.005, 0.009), g: 0.05, drag: 0.975, light: 68, trail: true,
        });
      }
    } else if (type === "chrys") {
      for (let i = 0; i < 90 * k; i++) {
        const a = rnd(0, Math.PI * 2), s = rnd(1.8, 4.8) * sp;
        addSpark(x, y, Math.cos(a) * s, Math.sin(a) * s, hue + rnd(-6, 6), { decay: rnd(0.008, 0.014), trail: true });
      }
    } else {
      for (let i = 0; i < 80 * k; i++) {
        const a = rnd(0, Math.PI * 2), s = rnd(2.8, 5) * sp;
        addSpark(x, y, Math.cos(a) * s, Math.sin(a) * s, hue);
      }
      const m = Math.round(40 * k), h2 = (hue + 45) % 360;
      for (let i = 0; i < m; i++) {
        const a = (i / m) * Math.PI * 2;
        addSpark(x, y, Math.cos(a) * 2.2 * sp, Math.sin(a) * 2.2 * sp, h2, { decay: 0.013, light: 70 });
      }
    }
  };

  const launch = (x, targetY) => {
    const { w, h, horizon } = dims.current;
    const y0 = horizon - 4;
    const ty = targetY ?? rnd(h * 0.07, Math.max(h * 0.1, horizon * 0.62));
    const dist = Math.max(y0 - ty, 120);
    rockets.current.push({
      x: x ?? rnd(w * 0.04, w * 0.96),
      y: y0,
      vx: rnd(-0.5, 0.5),
      vy: -dist * 0.014 * 1.12,
      ty,
    });
  };

  useImperativeHandle(ref, () => ({
    burst: (x, y) => explode(x, y),
    volley: (n = 10, gap = 140) => {
      for (let i = 0; i < n; i++) setTimeout(() => launch(), i * gap);
    },
  }));

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let raf;

    const resize = () => {
      const w = canvas.clientWidth, h = canvas.clientHeight;
      const ground = canvas.parentElement.querySelector(".ds-ground");
      const g = ground ? ground.clientHeight : h * 0.36;
      dims.current = { w, h, horizon: h - g };
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const tick = () => {
      const { w, h } = dims.current;

      ctx.globalCompositeOperation = "destination-out";
      ctx.fillStyle = "rgba(0,0,0,0.22)";
      ctx.fillRect(0, 0, w, h);
      ctx.globalCompositeOperation = "lighter";

      // sky flashes
      flashes.current = flashes.current.filter((f) => f.life > 0);
      for (const f of flashes.current) {
        const grd = ctx.createRadialGradient(f.x, f.y, 0, f.x, f.y, 300);
        grd.addColorStop(0, `hsla(${f.hue}, 100%, 70%, ${f.life * 0.15})`);
        grd.addColorStop(1, `hsla(${f.hue}, 100%, 60%, 0)`);
        ctx.fillStyle = grd;
        ctx.fillRect(f.x - 300, f.y - 300, 600, 600);
        f.life -= 0.07;
      }

      // rockets
      rockets.current = rockets.current.filter((r) => {
        r.x += r.vx;
        r.y += r.vy;
        r.vy *= 0.986;
        addSpark(r.x, r.y, rnd(-0.3, 0.3), rnd(0.2, 0.8), 40, { decay: 0.05, size: 1.2, g: 0.01, light: 75 });
        ctx.beginPath();
        ctx.fillStyle = "hsla(45, 100%, 85%, 1)";
        ctx.arc(r.x, r.y, 2, 0, Math.PI * 2);
        ctx.fill();
        if (r.y <= r.ty) {
          explode(r.x, r.y);
          return false;
        }
        return true;
      });

      // sparks
      sparks.current = sparks.current.filter((p) => p.life > 0);
      for (const p of sparks.current) {
        p.vx *= p.drag;
        p.vy = p.vy * p.drag + p.g;
        p.x += p.vx;
        p.y += p.vy;
        p.life -= p.decay;
        if (p.trail && Math.random() < 0.3 && p.life > 0.2) {
          addSpark(p.x, p.y, rnd(-0.2, 0.2), rnd(-0.1, 0.4), p.hue, { decay: 0.035, size: 0.9, g: 0.03, light: 70 });
        }
        let alpha = Math.max(p.life, 0);
        if (p.life < 0.35 && Math.random() < 0.3) alpha *= 0.4;
        ctx.beginPath();
        ctx.fillStyle = `hsla(${p.hue}, 100%, ${p.light}%, ${alpha})`;
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      }

      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const timers = [];
    let interval;
    if (auto && !reduce) {
      for (let i = 0; i < 6; i++) timers.push(setTimeout(() => launch(), 200 + i * 170));
      interval = setInterval(() => {
        if (document.hidden) return;
        launch();
        if (Math.random() < 0.6) timers.push(setTimeout(() => launch(), 180));
        if (Math.random() < 0.3) timers.push(setTimeout(() => launch(), 360));
      }, 650);
    }

    return () => {
      cancelAnimationFrame(raf);
      clearInterval(interval);
      timers.forEach(clearTimeout);
      ro.disconnect();
    };
  }, [auto]);

  return <canvas ref={canvasRef} className="ds-fireworks" aria-hidden="true" />;
});

/* ═════════════════════════ Scene pieces ═════════════════════════ */
const STARS = Array.from({ length: 130 }, () => ({
  x: Math.random() * 100,
  y: Math.random() * 62,
  s: Math.random() < 0.15 ? 2.6 : Math.random() < 0.5 ? 1.8 : 1.1,
  d: 2 + Math.random() * 4,
  o: -Math.random() * 6,
}));

const Stars = () => (
  <div className="ds-stars" aria-hidden="true">
    {STARS.map((s, i) => (
      <i key={i} style={{ left: `${s.x}%`, top: `${s.y}%`, width: s.s, height: s.s, animationDuration: `${s.d}s`, animationDelay: `${s.o}s` }} />
    ))}
  </div>
);

const Mountains = () => (
  <svg className="ds-mountains" viewBox="0 0 1600 150" preserveAspectRatio="none" aria-hidden="true">
    <path d="M0 150 L0 92 L120 62 L240 96 L380 50 L520 100 L700 72 L880 106 L1060 56 L1240 96 L1400 66 L1600 100 L1600 150Z" fill="#17335a" opacity=".85" />
    <path d="M0 150 L0 120 L160 102 L330 126 L520 108 L760 128 L980 104 L1180 126 L1400 100 L1600 122 L1600 150Z" fill="#0f2447" />
  </svg>
);

/* Nagara-style spire: straight sides curving to a tip, stepped ridges, amalaka and kalash */
const spireHalf = (w, t) => (w / 2) * Math.pow(1 - Math.pow(t, 1.4), 0.75);

const Shikhara = ({ x, base, w, h, flag = false }) => {
  const steps = 22;
  const left = [];
  const right = [];
  for (let i = 0; i <= steps; i++) {
    const t = (i / steps) * 0.97;
    const y = (base - t * h).toFixed(1);
    const half = spireHalf(w, t);
    left.push(`${(x - half).toFixed(1)} ${y}`);
    right.unshift(`${(x + half).toFixed(1)} ${y}`);
  }
  const d = `M${left.join(" L")} L${right.join(" L")} Z`;
  const topY = base - h;
  const amY = base - 0.9 * h;
  return (
    <g>
      <path d={d} fill="url(#ds-spire)" />
      {[0.14, 0.28, 0.42, 0.56, 0.7, 0.82].map((t) => {
        const y = base - t * h;
        const half = spireHalf(w, t);
        return (
          <g key={t}>
            <line x1={x - half - 1.5} x2={x + half + 1.5} y1={y} y2={y} stroke="#3a1c34" strokeOpacity=".5" strokeWidth={Math.max(1, w * 0.018)} />
            <line x1={x - half} x2={x + half} y1={y + 1.8} y2={y + 1.8} stroke="#ffe2c4" strokeOpacity=".22" strokeWidth={Math.max(0.8, w * 0.012)} />
          </g>
        );
      })}
      <line x1={x} x2={x} y1={base} y2={topY + h * 0.06} stroke="#ffe2c4" strokeOpacity=".2" strokeWidth="1.5" />
      <ellipse cx={x} cy={amY} rx={w * 0.15} ry={w * 0.05} fill="#e9b96e" />
      <line x1={x} x2={x} y1={amY} y2={topY - 4} stroke="#e9b96e" strokeWidth={Math.max(1.4, w * 0.02)} />
      <circle cx={x} cy={topY - 6} r={Math.max(2.4, w * 0.035)} fill="#ffd36a" />
      {flag && (
        <>
          <line x1={x} x2={x} y1={topY - 8} y2={topY - 34} stroke="#e9b96e" strokeWidth="1.6" />
          <path className="ds-flag" d={`M${x} ${topY - 34} L${x + 34} ${topY - 27} L${x} ${topY - 20}Z`} fill="#ff7a18" />
        </>
      )}
    </g>
  );
};

const archPath = (xc, bottom, top, half) => `M${xc - half} ${bottom} V${top + half * 1.2} Q${xc} ${top} ${xc + half} ${top + half * 1.2} V${bottom}Z`;
const mirror = (center, offsets) => offsets.flatMap((o) => [center - o, center + o]);

/* Ram Mandir, Ayodhya: three-storey Nagara-style temple with a central shikhara and saffron flag */
const Temple = () => (
  <svg className="ds-temple" viewBox="0 -26 700 446" preserveAspectRatio="xMinYMax meet" aria-hidden="true">
    <defs>
      <linearGradient id="ds-tstone" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#d9a38c" />
        <stop offset="1" stopColor="#7c5260" />
      </linearGradient>
      <linearGradient id="ds-spire" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stopColor="#8c5c68" />
        <stop offset="0.45" stopColor="#e3b49e" />
        <stop offset="1" stopColor="#8a5a66" />
      </linearGradient>
      <radialGradient id="ds-tglow">
        <stop offset="0" stopColor="#ffb25a" stopOpacity=".45" />
        <stop offset="1" stopColor="#ff8a3c" stopOpacity="0" />
      </radialGradient>
    </defs>

    {/* warm glow */}
    <ellipse cx="350" cy="385" rx="390" ry="80" fill="url(#ds-tglow)" />
    <circle cx="350" cy="120" r="150" fill="url(#ds-tglow)" opacity=".5" />

    {/* plinth and steps */}
    <rect x="30" y="396" width="640" height="24" fill="#8a5a62" />
    <rect x="60" y="384" width="580" height="14" fill="#a06f70" />
    <rect x="90" y="372" width="520" height="14" fill="#b98378" />
    <rect x="300" y="372" width="100" height="48" fill="#c99484" />
    {[384, 396, 408].map((y) => (
      <line key={y} x1="300" x2="400" y1={y} y2={y} stroke="#3a1c34" strokeOpacity=".3" strokeWidth="1.5" />
    ))}

    {/* ground floor colonnade */}
    <rect x="110" y="300" width="480" height="74" fill="url(#ds-tstone)" />
    <rect x="100" y="294" width="500" height="8" fill="#b57f78" />
    {mirror(350, [38, 76, 114, 152, 190]).map((xc, i) => (
      <path key={xc} className="ds-lit" style={{ animationDelay: `${-(i % 5) * 0.7}s` }} d={archPath(xc, 372, 322, 11)} fill="#ffb347" />
    ))}
    <path className="ds-lit" d="M335 372 V336 Q350 308 365 336 V372Z" fill="#ffcf70" />

    {/* first floor */}
    <rect x="150" y="238" width="400" height="58" fill="url(#ds-tstone)" />
    <rect x="140" y="232" width="420" height="8" fill="#b57f78" />
    {mirror(350, [19, 57, 95, 133, 171]).map((xc, i) => (
      <path key={xc} className="ds-lit" style={{ animationDelay: `${-(i % 4) * 0.9}s` }} d={archPath(xc, 290, 248, 8)} fill="#ffc46a" />
    ))}

    {/* second floor */}
    <rect x="200" y="186" width="300" height="46" fill="url(#ds-tstone)" />
    <rect x="190" y="180" width="320" height="8" fill="#b57f78" />
    {mirror(350, [30, 70, 110]).map((xc, i) => (
      <path key={xc} className="ds-lit" style={{ animationDelay: `${-i * 0.8}s` }} d={archPath(xc, 226, 194, 6)} fill="#ffc46a" />
    ))}

    {/* sanctum block */}
    <rect x="284" y="146" width="132" height="36" fill="url(#ds-tstone)" />
    <rect x="276" y="140" width="148" height="8" fill="#b57f78" />
    <path className="ds-lit" d={archPath(350, 180, 152, 9)} fill="#ffd27a" />

    {/* spires, back to front */}
    <Shikhara x={350} base={140} w={100} h={124} flag />
    <Shikhara x={262} base={180} w={60} h={80} />
    <Shikhara x={438} base={180} w={60} h={80} />
    <Shikhara x={178} base={232} w={56} h={70} />
    <Shikhara x={522} base={232} w={56} h={70} />
    <Shikhara x={124} base={294} w={48} h={62} />
    <Shikhara x={576} base={294} w={48} h={62} />
  </svg>
);

const Mandala = ({ cx, cy, r }) => (
  <g transform={`translate(${cx} ${cy}) scale(1 .27)`}>
    <circle r={r * 1.34} fill="none" stroke="rgba(255,225,190,.16)" strokeWidth={r * 0.012} />
    <circle r={r * 1.16} fill="none" stroke="rgba(255,225,190,.24)" strokeWidth={r * 0.012} strokeDasharray={`${r * 0.04} ${r * 0.05}`} />
    <circle r={r} fill="#1d4565" />
    <circle r={r} fill="none" stroke="#e9b44c" strokeWidth={r * 0.04} />
    <circle r={r * 0.86} fill="none" stroke="#e9b44c" strokeWidth={r * 0.012} strokeDasharray={`${r * 0.03} ${r * 0.04}`} />
    {Array.from({ length: 12 }).map((_, i) => (
      <ellipse key={i} cx="0" cy={-r * 0.5} rx={r * 0.1} ry={r * 0.22} transform={`rotate(${i * 30})`} fill={i % 2 ? "#f08a3c" : "#e8503a"} opacity=".95" />
    ))}
    {Array.from({ length: 8 }).map((_, i) => (
      <ellipse key={`b${i}`} cx="0" cy={-r * 0.26} rx={r * 0.075} ry={r * 0.16} transform={`rotate(${i * 45 + 22.5})`} fill="#ffe3a8" />
    ))}
    <circle r={r * 0.1} fill="#fff0cc" stroke="#e9b44c" strokeWidth={r * 0.02} />
  </g>
);

const FAR_ROW = Array.from({ length: 11 }, (_, i) => {
  const t = i / 10;
  return { x: 1520 - t * 880, y: 318 - t * 268, s: 0.62 - t * 0.5 };
});
const NEAR = [
  { x: 250, y: 292, s: 1.35 },
  { x: 640, y: 232, s: 0.95 },
  { x: 92, y: 196, s: 0.78 },
  { x: 430, y: 162, s: 0.6 },
];
const DIYAS = [...FAR_ROW, ...NEAR]
  .sort((a, b) => a.y - b.y)
  .map((d, i) => ({ ...d, delay: -(i % 6) * 0.3 }));

const Ground = () => (
  <svg className="ds-ground" viewBox="0 0 1600 360" preserveAspectRatio="xMidYMax slice" aria-hidden="true">
    <defs>
      <linearGradient id="ds-floor" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#9a6a72" />
        <stop offset="0.35" stopColor="#6e4560" />
        <stop offset="1" stopColor="#2e1c38" />
      </linearGradient>
      <linearGradient id="ds-bowl" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#e08a45" />
        <stop offset="1" stopColor="#8a3420" />
      </linearGradient>
      <radialGradient id="ds-oil" cx="50%" cy="50%" r="50%">
        <stop offset="0" stopColor="#ffd98a" />
        <stop offset="1" stopColor="#d9852f" />
      </radialGradient>
      <linearGradient id="ds-fl" x1="0" y1="1" x2="0" y2="0">
        <stop offset="0" stopColor="#ff8a1f" />
        <stop offset="0.6" stopColor="#ffc933" />
        <stop offset="1" stopColor="#fff3b0" />
      </linearGradient>
      <radialGradient id="ds-pool">
        <stop offset="0" stopColor="#ffb347" stopOpacity=".55" />
        <stop offset="1" stopColor="#ff7a18" stopOpacity="0" />
      </radialGradient>
      <radialGradient id="ds-fglow">
        <stop offset="0" stopColor="#ffd166" stopOpacity=".7" />
        <stop offset="1" stopColor="#ff9a3c" stopOpacity="0" />
      </radialGradient>
    </defs>

    <rect width="1600" height="360" fill="url(#ds-floor)" />

    {/* distant lamp line along the horizon */}
    {Array.from({ length: 30 }).map((_, i) => (
      <circle key={i} cx={420 + i * 40} cy={7 + (i % 3)} r="2" fill="#ffb347" opacity={0.5 + (i % 4) * 0.12} />
    ))}

    {/* rangoli */}
    <Mandala cx={1350} cy={92} r={90} />
    <Mandala cx={640} cy={150} r={170} />
    <Mandala cx={1150} cy={252} r={300} />

    {/* diyas, far to near */}
    {DIYAS.map((d, i) => (
      <g key={i} transform={`translate(${d.x} ${d.y}) scale(${d.s})`}>
        <ellipse className="ds-pool" cx="0" cy="8" rx="170" ry="38" fill="url(#ds-pool)" />
        <path d="M-60 0 Q-58 52 0 56 Q58 52 60 0Z" fill="url(#ds-bowl)" />
        <path d="M-46 20 Q0 42 46 20" stroke="#ffd28a" strokeOpacity=".5" strokeWidth="2" fill="none" />
        <ellipse cx="0" cy="0" rx="60" ry="17" fill="#f2b36a" />
        <ellipse cx="0" cy="2" rx="50" ry="12" fill="url(#ds-oil)" />
        <circle className="ds-pool" cx="0" cy="-24" r="40" fill="url(#ds-fglow)" />
        <g className="ds-sflame" style={{ animationDelay: `${d.delay}s` }}>
          <path d="M0 -52 C12 -32 10 -14 0 -4 C-10 -14 -12 -32 0 -52Z" fill="url(#ds-fl)" />
          <path d="M0 -28 C4 -22 3 -14 0 -10 C-3 -14 -4 -22 0 -28Z" fill="#fff8dc" opacity=".9" />
        </g>
      </g>
    ))}
  </svg>
);

/* ═════════════════════════ Login ═════════════════════════ */
const Login = () => {
  const navigate = useNavigate();
  const fireworks = useRef(null);
  const pageRef = useRef(null);

  const [errMsg, setErrMsg] = useState("");
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [inputState, setInputState] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const emailOk = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!emailOk) newErrors.email = "Please enter a valid email address";

    if (!formData.password) newErrors.password = "Password is required";
    else if (formData.password.length < 6) newErrors.password = "Password must be at least 6 characters";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Fetch DPR role after successful HRMS login. Throws on failure so handleSubmit's catch runs.
  const fetchDPRRole = async (token) => {
    const res = await axios.get(`${BASE_URL}/dpr/my-role/`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    sessionStorage.setItem("dpr_role", res.data.role);
    sessionStorage.setItem("dpr_name", res.data.full_name);
    sessionStorage.setItem("dpr_email", res.data.user_email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setErrMsg("");
    const loginToastId = toast.loading("Signing you in...", { position: "top-center", theme: "colored" });

    try {
      const res = await axios.post(`${BASE_URL}/user/login/`, formData);
      if (res.status === 200) {
        const { access, refresh, payload, payload_a } = res.data;

        // STEP 1: existing HRMS session data (unchanged)
        const sessionData = {
          access_token: access,
          refresh_token: refresh,
          role: payload.role,
          email: payload.email,
          employee_id: payload_a.emp_code,
          profilepic: payload_a.profilepic,
          reportinghead: payload_a.reporting_head,
          department: payload_a.department_name,
          departmentid: payload_a.department,
          company: payload_a.division_name,
          company_id: payload_a.sub_company_id,
          name: payload_a.name,
        };
        Object.entries(sessionData).forEach(([k, v]) => sessionStorage.setItem(k, v));

        // STEP 2: DPR role
        await fetchDPRRole(access);

        toast.dismiss(loginToastId);
        setSuccess(true);
        fireworks.current?.volley(18, 120);
        toast.success("Happy Diwali! Taking you to your dashboard 🪔", {
          position: "top-center",
          autoClose: 2000,
          theme: "colored",
        });
        setTimeout(() => window.location.reload(), 2400);
      } else {
        toast.dismiss(loginToastId);
        setErrMsg("Invalid email or password");
      }
    } catch (err) {
      toast.dismiss(loginToastId);
      const apiError = err.response?.data?.error;
      setErrMsg(apiError || err.response?.data?.detail || "Login failed");

      if (err.response) {
        const data = err.response.data;
        if (typeof data?.error === "string") {
          toast.error(`Error: ${data.error}`, { position: "top-center", theme: "colored" });
        } else if (data && typeof data === "object") {
          Object.entries(data).forEach(([field, messages]) =>
            [].concat(messages).forEach((m) => toast.error(`${field}: ${m}`, { position: "top-center", theme: "colored" }))
          );
        } else {
          toast.error("Authentication failed!", { position: "top-center", theme: "colored" });
        }
      } else {
        toast.error("Network error. Please try again.", { position: "top-center", theme: "colored" });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputState((s) => ({ ...s, [name]: value.trim() ? "valid" : "" }));
    setFormData((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Tap the sky for an instant firework
  const handlePageClick = (e) => {
    if (e.target.closest("a, button, input, .ds-card")) return;
    const r = pageRef.current.getBoundingClientRect();
    fireworks.current?.burst(e.clientX - r.left, e.clientY - r.top);
  };

  return (
    <>
      <div className="ds-page" ref={pageRef} onClick={handlePageClick}>
        <div className="ds-sky" aria-hidden="true" />
        <Stars />
        <FireworksCanvas ref={fireworks} />
        <Mountains />
        <Temple />
        <div className="ds-mist" aria-hidden="true" />
        <Ground />

        <div className="ds-content">
          {/* ── Greeting ── */}
          <section className="ds-left">
            <Link to="/" className="ds-logo-link">
              <img src={Logo} alt="Company logo" className="ds-logo" />
            </Link>

            <motion.h1
              className="ds-title"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.2, 0.8, 0.2, 1] }}
            >
              Happy Diwali
            </motion.h1>

            <motion.div
              className="ds-rule"
              aria-hidden="true"
              initial={{ opacity: 0, scaleX: 0.4 }}
              animate={{ opacity: 1, scaleX: 1 }}
              transition={{ duration: 0.9, delay: 0.5 }}
            >
              <span /><b>✦</b><span />
            </motion.div>

            <motion.p
              className="ds-wish"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.9, delay: 0.8 }}
            >
              Best wishes from DPR Team
            </motion.p>
          </section>

          {/* ── Login card ── */}
          <section className="ds-right">
            <motion.main
              className="ds-card"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
            >
              <div className="ds-head">
                <h2>Welcome back</h2>
                <p>Sign in to your DPR Portal dashboard</p>
              </div>

              <form onSubmit={handleSubmit} className="ds-form" noValidate>
                <div className="ds-field">
                  <label htmlFor="email">Email address</label>
                  <div className={`ds-input ${errors.email ? "err" : inputState.email ? "ok" : ""}`}>
                    <Mail size={17} className="ds-lead" />
                    <input
                      type="text"
                      id="email"
                      name="email"
                      placeholder="you@company.com"
                      autoComplete="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      disabled={isLoading}
                    />
                    {emailOk && !errors.email && <CheckCircle size={17} className="ds-check" />}
                  </div>
                  {errors.email && <span className="ds-error-text">{errors.email}</span>}
                </div>

                <div className="ds-field">
                  <div className="ds-label-row">
                    <label htmlFor="password">Password</label>
                    <button type="button" className="ds-link" onClick={() => navigate("/forgotpassword")} disabled={isLoading}>
                      Forgot password?
                    </button>
                  </div>
                  <div className={`ds-input ${errors.password ? "err" : inputState.password ? "ok" : ""}`}>
                    <Lock size={17} className="ds-lead" />
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      value={formData.password}
                      onChange={handleInputChange}
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      className="ds-eye"
                      onClick={() => setShowPassword((s) => !s)}
                      disabled={isLoading}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {errors.password && <span className="ds-error-text">{errors.password}</span>}
                </div>

                <motion.button type="submit" className="ds-submit" disabled={isLoading} whileTap={{ scale: 0.98 }}>
                  <span>{isLoading ? "Signing in..." : success ? "Welcome in" : "Sign in"}</span>
                  {!isLoading && <ArrowRight size={18} />}
                </motion.button>

                {errMsg && (
                  <div className="ds-alert" role="alert">
                    <Shield size={16} />
                    <span>{errMsg}</span>
                  </div>
                )}
              </form>
            </motion.main>
          </section>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,600;0,9..144,700;1,9..144,500&family=Manrope:wght@400;500;600;700&display=swap');

        /* Guard against global CSS: project doesn't use border-box */
        .ds-page, .ds-page *, .ds-page *::before, .ds-page *::after { box-sizing: border-box; }

        /* Lock fonts so global p / label / input / button rules can't override them */
        .ds-page p, .ds-page label, .ds-page input, .ds-page button, .ds-page span, .ds-page a, .ds-page b {
          font-family: 'Manrope', system-ui, sans-serif;
        }
        .ds-page .ds-title, .ds-page .ds-wish, .ds-page .ds-head h2 { font-family: 'Fraunces', Georgia, serif; }

        .ds-page {
          --g: clamp(220px, 36vh, 400px);
          --gold: #ffc933;
          --cream: #fff3d6;
          position: relative;
          min-height: 100vh;
          min-height: 100svh;
          overflow: hidden;
          color: var(--cream);
          background: #050d26;
        }
        .ds-page h1, .ds-page h2, .ds-page p { margin: 0; }

        /* sky */
        .ds-sky {
          position: absolute; inset: 0; z-index: 0;
          background:
            radial-gradient(ellipse 60% 26% at 18% 66%, rgba(255,150,70,.30), transparent 70%),
            radial-gradient(ellipse 90% 22% at 60% 68%, rgba(255,130,80,.14), transparent 70%),
            linear-gradient(180deg, #050d26 0%, #0a2147 34%, #12426a 62%, #2c6080 72%);
        }
        .ds-stars { position: absolute; inset: 0; z-index: 1; pointer-events: none; }
        .ds-stars i {
          position: absolute; border-radius: 50%; background: #fff6d8; opacity: .3;
          animation: ds-tw 3s ease-in-out infinite;
        }
        @keyframes ds-tw { 0%,100% { opacity: .2; } 50% { opacity: 1; } }

        .ds-fireworks { position: absolute; inset: 0; width: 100%; height: 100%; z-index: 2; pointer-events: none; }

        /* skyline */
        .ds-mountains {
          position: absolute; left: 0; width: 100%; z-index: 3; pointer-events: none;
          bottom: calc(var(--g) - 4px); height: clamp(80px, 15vh, 150px);
        }
        .ds-temple {
          position: absolute; left: 1vw; z-index: 3; pointer-events: none;
          bottom: calc(var(--g) - 14px);
          height: clamp(180px, 40vh, 420px); aspect-ratio: 700 / 446; width: auto; max-width: 54vw;
        }
        .ds-flag { transform-box: fill-box; transform-origin: 0% 50%; animation: ds-wave 2.2s ease-in-out infinite; }
        @keyframes ds-wave {
          0%,100% { transform: skewY(-7deg) scaleX(1); }
          50% { transform: skewY(7deg) scaleX(.9); }
        }
        .ds-lit { animation: ds-lamp 3.4s ease-in-out infinite; }
        @keyframes ds-lamp { 0%,100% { opacity: .85; } 50% { opacity: 1; } }

        .ds-mist {
          position: absolute; left: 0; right: 0; z-index: 4; pointer-events: none;
          bottom: calc(var(--g) - 36px); height: 72px;
          background: linear-gradient(180deg, transparent, rgba(255,170,100,.22), transparent);
        }
        .ds-ground {
          position: absolute; left: 0; bottom: 0; z-index: 4; pointer-events: none;
          width: 100%; height: var(--g); display: block;
        }
        .ds-sflame { transform-box: fill-box; transform-origin: 50% 100%; animation: ds-flick 1.6s ease-in-out infinite; }
        .ds-pool { animation: ds-pulse 2.6s ease-in-out infinite; }
        @keyframes ds-flick {
          0%,100% { transform: scale(1,1) skewX(0deg); }
          25% { transform: scale(.94,1.07) skewX(-3deg); }
          50% { transform: scale(1.05,.96) skewX(2deg); }
          75% { transform: scale(.97,1.04) skewX(-1deg); }
        }
        @keyframes ds-pulse { 0%,100% { opacity: .75; } 50% { opacity: 1; } }

        /* layout */
        .ds-content {
          position: relative; z-index: 6; min-height: 100vh; min-height: 100svh;
          display: grid; grid-template-columns: 1.1fr .9fr; align-items: center; gap: 40px;
          padding: 36px 64px calc(var(--g) * .62) 72px;
          pointer-events: none;
        }
        .ds-left, .ds-right { pointer-events: none; }
        .ds-logo-link, .ds-card { pointer-events: auto; }

        .ds-left { display: flex; flex-direction: column; align-items: flex-start; align-self: start; padding-top: clamp(0px, 5vh, 56px); }
        .ds-logo-link { display: inline-block; margin-bottom: 36px; }
        .ds-logo { display: block; width: 124px; height: auto; filter: brightness(0) invert(1); }

        .ds-title {
          font-weight: 700; letter-spacing: -0.03em; line-height: 1.02;
          font-size: clamp(54px, 7.4vw, 120px);
          background: linear-gradient(180deg, #fff6c4 0%, #ffcf4a 48%, #f08a1f 100%);
          -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
          filter: drop-shadow(0 0 26px rgba(255,170,40,.4));
          padding-bottom: .06em;
        }
        .ds-rule { display: flex; align-items: center; gap: 14px; width: min(420px, 80%); margin: 18px 0 16px; transform-origin: left center; }
        .ds-rule span { flex: 1; height: 1px; background: linear-gradient(90deg, rgba(255,201,51,0), rgba(255,201,51,.85)); }
        .ds-rule span:last-child { background: linear-gradient(90deg, rgba(255,201,51,.85), rgba(255,201,51,0)); }
        .ds-rule b { color: var(--gold); font-weight: 400; font-size: 16px; }
        .ds-wish { font-style: italic; font-weight: 500; font-size: clamp(20px, 2.2vw, 30px); color: #ffe7b8; letter-spacing: .01em; text-shadow: 0 2px 18px rgba(0,0,0,.45); }

        .ds-right { display: grid; place-items: center; }
        .ds-card {
          width: 100%; max-width: 420px; padding: 34px 32px 30px;
          border-radius: 24px;
          background: rgba(8,18,44,.58);
          backdrop-filter: blur(20px) saturate(140%);
          -webkit-backdrop-filter: blur(20px) saturate(140%);
          border: 1px solid rgba(255,214,140,.28);
          box-shadow: 0 30px 70px rgba(0,0,0,.45), inset 0 1px 0 rgba(255,235,190,.18);
        }
        .ds-head { margin-bottom: 26px; }
        .ds-head h2 { font-weight: 700; font-size: 32px; letter-spacing: -0.02em; margin-bottom: 6px !important; color: #fff3d6; }
        .ds-head p { font-size: 15px; color: rgba(255,243,214,.68); }

        .ds-form { display: flex; flex-direction: column; gap: 18px; }
        .ds-field { display: flex; flex-direction: column; gap: 7px; }
        .ds-field label { margin: 0; font-size: 14px; font-weight: 600; color: rgba(255,243,214,.9); }
        .ds-label-row { display: flex; justify-content: space-between; align-items: center; }

        .ds-input {
          position: relative; overflow: hidden; border-radius: 12px;
          background: rgba(255,255,255,.07); border: 1px solid rgba(255,255,255,.16);
          transition: border-color .2s, box-shadow .2s, background .2s;
        }
        .ds-input:focus-within { border-color: var(--gold); background: rgba(255,255,255,.1); box-shadow: 0 0 0 4px rgba(255,201,51,.16); }
        .ds-input.ok { border-color: rgba(94,230,160,.45); }
        .ds-input.err { border-color: #ff6b7d; background: rgba(255,80,100,.09); }
        .ds-lead { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: rgba(255,225,170,.6); pointer-events: none; }
        .ds-input input {
          width: 100%; max-width: 100%; height: auto; margin: 0; padding: 13px 44px 13px 42px;
          border: none; outline: none; background: transparent; box-shadow: none;
          font-size: 15px; color: #fff; border-radius: 12px;
        }
        .ds-input input::placeholder { color: rgba(255,243,214,.38); }
        .ds-input input:disabled { opacity: .6; cursor: not-allowed; }
        /* Chrome autofill paints inputs light blue; repaint with the theme */
        .ds-input input:-webkit-autofill,
        .ds-input input:-webkit-autofill:hover,
        .ds-input input:-webkit-autofill:focus {
          -webkit-box-shadow: 0 0 0 100px #14203f inset;
          -webkit-text-fill-color: #fff;
          caret-color: #fff;
          transition: background-color 9999s ease-in-out 0s;
        }
        .ds-check { position: absolute; right: 14px; top: 50%; transform: translateY(-50%); color: #5ee6a0; }
        .ds-eye {
          position: absolute; right: 6px; top: 50%; transform: translateY(-50%);
          border: none; background: transparent; color: rgba(255,225,170,.6); cursor: pointer; padding: 8px; border-radius: 8px; line-height: 0;
        }
        .ds-eye:hover:not(:disabled) { color: var(--gold); background: rgba(255,255,255,.08); }
        .ds-error-text { font-size: 13px; color: #ff9aa6; }

        .ds-link {
          border: none; background: none; padding: 0; cursor: pointer;
          font-size: 13px; font-weight: 600; color: var(--gold);
          text-decoration: underline; text-underline-offset: 3px; text-decoration-color: transparent; transition: text-decoration-color .2s;
        }
        .ds-link:hover:not(:disabled) { text-decoration-color: currentColor; }

        .ds-submit {
          display: flex; align-items: center; justify-content: center; gap: 8px;
           height: 52px; margin-top: 4px; padding: 0 20px;
          border: none; border-radius: 12px; cursor: pointer;
          font-size: 16px; font-weight: 700; color: #2a1206;
          background: linear-gradient(100deg, #ffd96a 0%, #ffab3c 60%, #ff8a1f 100%);
          box-shadow: 0 10px 28px -8px rgba(255,150,40,.55), inset 0 1px 0 rgba(255,255,255,.5);
          transition: filter .2s, box-shadow .2s;
        }
        .ds-submit:hover:not(:disabled) { filter: brightness(1.06); box-shadow: 0 14px 32px -8px rgba(255,150,40,.7), inset 0 1px 0 rgba(255,255,255,.5); }
        .ds-submit:disabled { opacity: .7; cursor: not-allowed; }

        .ds-alert {
          display: flex; align-items: center; gap: 10px; padding: 12px 14px; border-radius: 12px;
          background: rgba(255,70,90,.13); border: 1px solid rgba(255,90,110,.42); color: #ffb9c1; font-size: 14px;
        }
        .ds-alert svg { flex-shrink: 0; }

        .ds-page button:focus-visible, .ds-page a:focus-visible, .ds-page input:focus-visible { outline: 3px solid var(--gold); outline-offset: 2px; }

        @media (max-width: 1024px) {
          .ds-content { grid-template-columns: 1fr; gap: 28px; padding: 28px 20px calc(var(--g) * .75); }
          .ds-left { align-items: center; text-align: center; }
          .ds-rule { transform-origin: center; }
          .ds-logo-link { margin-bottom: 22px; }
          .ds-temple { height: auto; width: 70vw; max-width: none; }
        }
        @media (max-width: 520px) {
          .ds-card { padding: 26px 20px 24px; }
          .ds-head h2 { font-size: 27px; }
          .ds-logo { width: 100px; }
          .ds-temple { width: 88vw; }
        }
        @media (prefers-reduced-motion: reduce) {
          .ds-stars i, .ds-lit, .ds-sflame, .ds-pool, .ds-flag { animation: none !important; }
        }
      `}</style>
    </>
  );
};

export default Login;